import { VueConstructor } from 'vue';

import { app } from '@/main';
import { isRootUser } from '@/utils/auth';
import { IBusinessType } from '@/models/business';
import { ValidatorState } from '@/router/validators/types';

import { Group } from './group.model';
import { EBusinessUserType } from '@/store/modules/businessTypes';

type ZendeskSettings = {
  greetings?: { online: string; offline: string };
  disabled?: boolean;
  position?: 'bl' | 'br';
  offsetVertical?: string;
  offsetHorizontal?: string;
};

type ZendeskProviders = {
  [k: string]: ZendeskSettings;
};

type ZendeskTargets = {
  mobile?: ZendeskSettings;
  desktop?: ZendeskSettings;
  providers?: ZendeskProviders;
};

type AuthRules = {
  authType: String[];
};

type Navbar = {
  name?: string;
  icon?: string;
  description?: string;
  hideLeftBar?: boolean;
  group?: any;
};

export type RouteMeta = {
  $group?: Group;

  roles?: Array<string>;
  requiresBusinessType?: Array<string>;
  requiresCountry?: Array<string>;
  requiresAuthFree?: boolean;
  validator?: () => ValidatorState;
  navbar?: Navbar;
  superRoot?: boolean;
  requiresAuth?: boolean;
  isResponsive?: boolean | string;
  flag?: string;
  customFlagValidator?: any;
  requiresBusiness?: boolean;
  widgetActiveRestriction?: boolean;
  onlyManager?: boolean;
  businessTypeRestriction?: IBusinessType[];
  countryRestriction?: string[];
  scrollToTop?: boolean;
  zendesk?: ZendeskTargets & { disabled?: boolean };
  authRules?: AuthRules;
  redirect?: { name: string };
  warnChanges?: boolean;
  pageView?: {
    eventLabel: string | (() => string);
    eventProps?(to?: Route): any;
  };
  disableFullLoader?: boolean;
  feature?: any;
  needProviderConnected?: boolean;
  hideFromMenu?(): boolean;
};

export class Route {
  constructor(
    public path: string,
    public name: string,
    public component?: VueConstructor | (() => Promise<typeof import('*.vue')>),
    public meta: RouteMeta = {},
    public children?: Route[]
  ) {}

  public isVisible() {
    const { business = null } = app.$store.getters['business/getBusinessInfo'];

    if (!this.meta.navbar) {
      return false;
    }

    if (
      (isRootUser() || this.meta.superRoot) &&
      isRootUser() !== this.meta.superRoot
    ) {
      return false;
    }

    if (
      business &&
      this.meta.requiresCountry &&
      !this.meta.requiresCountry.includes(business.country)
    ) {
      return false;
    }

    if (
      this.meta.onlyManager &&
      app.$store.getters['business/getManagerRole'] !== EBusinessUserType.OWNER
    ) {
      return false;
    }

    if (
      this.meta.requiresBusinessType &&
      !this.meta.requiresBusinessType.includes(
        app.$store.getters['business/getBusinessType']
      )
    ) {
      return false;
    }

    if (
      this.meta.roles &&
      !this.meta.roles.includes(app.$store.getters['business/getManagerRole'])
    ) {
      return false;
    }

    if (this.meta.flag && !this.validateRouteFlag()) {
      return false;
    }

    if (this.meta.customFlagValidator && !this.meta.customFlagValidator()) {
      return false;
    }

    if (this.meta.validator && !this.meta.validator().isValid) {
      return false;
    }

    return true;
  }

  public getRouteValidationStatus() {
    return this.meta.validator && this.meta.validator();
  }

  private validateRouteFlag() {
    const { flag } = this.meta;

    if (flag === 'stripe_profile') {
      return app.$store.getters['stripe/getProfile'];
    }

    return app.$store.getters['flags/getFlags'].includes(flag);
  }
}
